/**
 * Button Component
 * Modern button with multiple variants and loading state
 */

import React from 'react';
import { Button as MuiButton, CircularProgress } from '@mui/material';
import { styled } from '@mui/material/styles';

const StyledButton = styled(MuiButton)(({ theme, customvariant }) => {
  const variants = {
    primary: {
      backgroundColor: theme.palette.accent?.main || '#26BAFF',
      color: '#fff',
      '&:hover': {
        backgroundColor: theme.palette.accent?.dark || '#0099DD',
        transform: 'translateY(-1px)',
      },
    },
    secondary: {
      backgroundColor: 'transparent',
      color: theme.palette.text.primary,
      border: `1.5px solid ${theme.palette.divider}`,
      '&:hover': {
        backgroundColor: theme.palette.action.hover,
        borderColor: theme.palette.primary.main,
      },
    },
    ghost: {
      backgroundColor: 'transparent',
      color: theme.palette.text.secondary,
      '&:hover': {
        backgroundColor: theme.palette.action.hover,
        color: theme.palette.primary.main,
      },
    },
    danger: {
      backgroundColor: theme.palette.error.main,
      color: '#fff',
      '&:hover': {
        backgroundColor: theme.palette.error.dark,
      },
    },
    'danger-outlined': {
      backgroundColor: 'transparent',
      color: theme.palette.error.main,
      border: `1.5px solid ${theme.palette.error.main}`,
      '&:hover': {
        backgroundColor: theme.palette.error.light,
      },
    },
    success: {
      backgroundColor: theme.palette.success.main,
      color: '#fff',
      '&:hover': {
        backgroundColor: theme.palette.success.dark,
      },
    },
  };

  return {
    borderRadius: 8,
    textTransform: 'none',
    fontWeight: 500,
    fontSize: '0.875rem',
    padding: '10px 20px',
    transition: 'all 150ms ease',
    boxShadow: 'none',
    '&:focus-visible': {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: 2,
    },
    '&:disabled': {
      opacity: 0.6,
      cursor: 'not-allowed',
    },
    ...(variants[customvariant] || variants.primary),
  };
});

const Button = ({
  children,
  variant = 'primary',
  loading = false,
  disabled = false,
  startIcon,
  endIcon,
  fullWidth = false,
  size = 'medium',
  onClick,
  type = 'button',
  className,
  ...props
}) => {
  const sizeStyles = {
    small: { padding: '6px 14px', fontSize: '0.8125rem' },
    medium: { padding: '10px 20px', fontSize: '0.875rem' },
    large: { padding: '12px 28px', fontSize: '1rem' },
  };

  return (
    <StyledButton
      customvariant={variant}
      disabled={disabled || loading}
      fullWidth={fullWidth}
      type={type}
      onClick={onClick}
      className={className}
      startIcon={loading ? null : startIcon}
      endIcon={loading ? null : endIcon}
      sx={sizeStyles[size]}
      disableElevation
      {...props}
    >
      {loading ? (
        <>
          <CircularProgress
            size={16}
            color="inherit"
            sx={{ mr: children ? 1 : 0 }}
          />
          {children}
        </>
      ) : (
        children
      )}
    </StyledButton>
  );
};

// Named exports for common button types
export const PrimaryButton = (props) => <Button variant="primary" {...props} />;
export const SecondaryButton = (props) => <Button variant="secondary" {...props} />;
export const GhostButton = (props) => <Button variant="ghost" {...props} />;
export const DangerButton = (props) => <Button variant="danger" {...props} />;
export const SuccessButton = (props) => <Button variant="success" {...props} />;

export default Button;
